import { useCallback, useEffect, useRef } from 'react'
import { useAppStore } from '@store/appStore'

/**
 * Navigation hook for camera switching with left/right/up/down gestures
 */
export function useNavigation() {
  const cameras = useAppStore((state) => state.cameras)
  const currentCameraIndex = useAppStore((state) => state.currentCameraIndex)
  const navigateToCamera = useAppStore((state) => state.navigateToCamera)
  const navigateNext = useAppStore((state) => state.navigateNext)
  const navigatePrevious = useAppStore((state) => state.navigatePrevious)
  
  // Enable automatic navigation recording
  useNavigationRecording()
  
  /**
   * Navigate to next camera (sequential, swipe left)
   */
  const goNext = useCallback(() => {
    console.log('📍 goNext: Navigating to next camera', {
      currentIndex: currentCameraIndex,
      totalCameras: cameras.length,
      canGoNext: currentCameraIndex < cameras.length - 1
    })
    navigateNext()
  }, [navigateNext, currentCameraIndex, cameras.length])
  
  /**
   * Navigate to previous camera (sequential, swipe right)
   */
  const goPrevious = useCallback(() => {
    console.log('📍 goPrevious: Navigating to previous camera', {
      currentIndex: currentCameraIndex,
      totalCameras: cameras.length,
      canGoPrevious: currentCameraIndex > 0
    })
    navigatePrevious()
  }, [navigatePrevious, currentCameraIndex, cameras.length])
  
  /**
   * Find camera at same position with different pitch angle
   * For up/down swipe navigation (spatial pitch-based)
   */
  const findCameraWithDifferentPitch = useCallback(
    (direction: 'up' | 'down'): number | null => {
      if (cameras.length === 0 || currentCameraIndex < 0) return null
      
      const currentCamera = cameras[currentCameraIndex]
      const currentPos = currentCamera.position
      
      // Convert quaternion to pitch angle
      const getPitchFromQuaternion = (q: [number, number, number, number]): number => {
        const [qw, qx, qy, qz] = q
        // Pitch = atan2(2*(qw*qx + qy*qz), 1 - 2*(qx^2 + qy^2))
        return Math.atan2(
          2 * (qw * qx + qy * qz),
          1 - 2 * (qx * qx + qy * qy)
        )
      }
      
      const currentPitch = getPitchFromQuaternion(currentCamera.rotation)
      
      // Position threshold (cameras within 0.5m are considered "same position")
      const positionThreshold = 0.5
      
      // Find cameras at similar position
      const nearbyCameras = cameras
        .map((cam, idx) => {
          const dx = cam.position[0] - currentPos[0]
          const dy = cam.position[1] - currentPos[1]
          const dz = cam.position[2] - currentPos[2]
          const distance = Math.sqrt(dx * dx + dy * dy + dz * dz)
          
          if (distance > positionThreshold || idx === currentCameraIndex) {
            return null
          }
          
          const pitch = getPitchFromQuaternion(cam.rotation)
          const pitchDiff = pitch - currentPitch
          
          return { index: idx, pitch, pitchDiff, distance }
        })
        .filter((c): c is NonNullable<typeof c> => c !== null)
      
      if (nearbyCameras.length === 0) return null
      
      // Find best candidate based on direction
      if (direction === 'up') {
        // Looking for camera with lower pitch (tilting up)
        const candidates = nearbyCameras
          .filter((c) => c.pitchDiff < 0)
          .sort((a, b) => a.pitchDiff - b.pitchDiff) // Most negative first
        
        return candidates[0]?.index ?? null
      } else {
        // Looking for camera with higher pitch (tilting down)
        const candidates = nearbyCameras
          .filter((c) => c.pitchDiff > 0)
          .sort((a, b) => b.pitchDiff - a.pitchDiff) // Most positive first
        
        return candidates[0]?.index ?? null
      }
    },
    [cameras, currentCameraIndex]
  )
  
  /**
   * Navigate up (find camera with lower pitch at same position)
   */
  const goUp = useCallback(() => {
    console.log('📍 goUp: Searching for camera with lower pitch')
    const targetIndex = findCameraWithDifferentPitch('up')
    if (targetIndex !== null) {
      console.log('✅ goUp: Found camera', { targetIndex })
      navigateToCamera(targetIndex)
      return true
    }
    console.log('❌ goUp: No camera found with lower pitch at this position')
    return false
  }, [findCameraWithDifferentPitch, navigateToCamera])
  
  /**
   * Navigate down (find camera with higher pitch at same position)
   */
  const goDown = useCallback(() => {
    console.log('📍 goDown: Searching for camera with higher pitch')
    const targetIndex = findCameraWithDifferentPitch('down')
    if (targetIndex !== null) {
      console.log('✅ goDown: Found camera', { targetIndex })
      navigateToCamera(targetIndex)
      return true
    }
    console.log('❌ goDown: No camera found with higher pitch at this position')
    return false
  }, [findCameraWithDifferentPitch, navigateToCamera])
  
  /**
   * Navigate to specific frame by index
   */
  const goToFrame = useCallback((frameIndex: number) => {
    if (frameIndex < 0 || frameIndex >= cameras.length) {
      console.warn('❌ goToFrame: Invalid frame index', { frameIndex, totalFrames: cameras.length })
      return false
    }
    navigateToCamera(frameIndex)
    return true
  }, [navigateToCamera, cameras.length])
  
  return {
    goNext,
    goPrevious,
    goUp,
    goDown,
    goToFrame,
    canGoNext: currentCameraIndex < cameras.length - 1,
    canGoPrevious: currentCameraIndex > 0,
    currentCamera: cameras[currentCameraIndex] || null,
    totalCameras: cameras.length,
  }
}

/**
 * Hook to automatically record navigation journeys (start→end frame ranges)
 * Uses 500ms debounce to group rapid frame changes into single history entries
 * Only records when not playing back (to avoid recording playback steps)
 */
export function useNavigationRecording() {
  const currentCameraIndex = useAppStore(state => state.currentCameraIndex);
  const manuallySelectedFrameIndex = useAppStore(state => state.manuallySelectedFrameIndex);
  const isPlaying = useAppStore(state => state.playback.isPlaying);
  const isNavigatingHistory = useAppStore(state => state.isNavigatingHistory);
  const isFrameSequenceAnimating = useAppStore(state => state.isFrameSequenceAnimating);
  const recordStep = useAppStore(state => state.recordNavigationStep);
  const cameras = useAppStore(state => state.cameras);
  const currentSectionId = useAppStore(state => state.currentSectionId);

  // Track navigation journey (start frame + debounce timer)
  const navigationJourneyRef = useRef<{
    startFrame: number;
    currentEndFrame: number;
    trigger: 'gesture' | 'button';
    timerId: NodeJS.Timeout | null;
  } | null>(null);

  const previousCameraIndexRef = useRef<number | null>(null);
  const previousManualIndexRef = useRef<number | null>(null);

  // Record the current navigation journey
  const recordJourney = useCallback((startFrame: number, endFrame: number, trigger: 'gesture' | 'button') => {
    if (startFrame < 0 || endFrame < 0 || !cameras || endFrame >= cameras.length) {
      console.warn('⚠️ [useNavigationRecording] Invalid journey:', { startFrame, endFrame });
      return;
    }

    // Don't record if start === end (no journey happened, just a single frame selection)
    if (startFrame === endFrame) {
      console.log(`⏭️  [useNavigationRecording] Skipping no-op journey: ${startFrame}→${endFrame}`);
      return;
    }

    recordStep({
      startFrame,
      endFrame,
      timestamp: Date.now(),
      trigger,
      viewportState: undefined,
      sectionId: currentSectionId
    });

    console.log(`📍 [useNavigationRecording] Recorded journey: ${startFrame}→${endFrame} (section ${currentSectionId}, ${trigger})`);
  }, [recordStep, cameras, currentSectionId]);

  useEffect(() => {
    // Skip recording if no cameras loaded
    if (!cameras || cameras.length === 0) {
      previousCameraIndexRef.current = currentCameraIndex;
      previousManualIndexRef.current = manuallySelectedFrameIndex;
      return;
    }

    // Skip recording during playback to avoid polluting history
    if (isPlaying) {
      // Cancel any pending journey recording
      if (navigationJourneyRef.current?.timerId) {
        clearTimeout(navigationJourneyRef.current.timerId);
        navigationJourneyRef.current = null;
      }
      previousCameraIndexRef.current = currentCameraIndex;
      previousManualIndexRef.current = manuallySelectedFrameIndex;
      return;
    }

    // Skip recording during history navigation to avoid corrupting history
    if (isNavigatingHistory) {
      // Cancel any pending journey recording
      if (navigationJourneyRef.current?.timerId) {
        clearTimeout(navigationJourneyRef.current.timerId);
        navigationJourneyRef.current = null;
      }
      previousCameraIndexRef.current = currentCameraIndex;
      previousManualIndexRef.current = manuallySelectedFrameIndex;
      return;
    }

    // Skip recording during frame sequence animation to avoid duplicate recordings
    // The animation's onComplete callback will handle recording the journey
    if (isFrameSequenceAnimating) {
      // Cancel any pending journey recording
      if (navigationJourneyRef.current?.timerId) {
        clearTimeout(navigationJourneyRef.current.timerId);
        navigationJourneyRef.current = null;
      }
      previousCameraIndexRef.current = currentCameraIndex;
      previousManualIndexRef.current = manuallySelectedFrameIndex;
      return;
    }

    // Determine which index changed and what trigger to use
    let changedFrame: number | null = null;
    let trigger: 'gesture' | 'button' = 'gesture';

    if (currentCameraIndex !== previousCameraIndexRef.current && currentCameraIndex !== null) {
      changedFrame = currentCameraIndex;
      trigger = 'gesture';
    } else if (manuallySelectedFrameIndex !== previousManualIndexRef.current && manuallySelectedFrameIndex !== null) {
      changedFrame = manuallySelectedFrameIndex;
      trigger = 'button';
    }

    if (changedFrame !== null && changedFrame >= 0 && changedFrame < cameras.length) {
      // Clear existing debounce timer
      if (navigationJourneyRef.current?.timerId) {
        clearTimeout(navigationJourneyRef.current.timerId);
      }

      // Start new journey if none exists
      if (!navigationJourneyRef.current) {
        navigationJourneyRef.current = {
          startFrame: changedFrame,
          currentEndFrame: changedFrame,
          trigger,
          timerId: null
        };
      } else {
        // Update end frame of existing journey
        navigationJourneyRef.current.currentEndFrame = changedFrame;
      }

      // Set 500ms debounce timer to record journey when settled
      const timerId = setTimeout(() => {
        if (navigationJourneyRef.current) {
          const { startFrame, currentEndFrame, trigger } = navigationJourneyRef.current;
          recordJourney(startFrame, currentEndFrame, trigger);
          navigationJourneyRef.current = null;
        }
      }, 500);

      navigationJourneyRef.current.timerId = timerId;
    }

    previousCameraIndexRef.current = currentCameraIndex;
    previousManualIndexRef.current = manuallySelectedFrameIndex;
  }, [currentCameraIndex, manuallySelectedFrameIndex, isPlaying, isNavigatingHistory, isFrameSequenceAnimating, recordJourney, cameras]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (navigationJourneyRef.current?.timerId) {
        clearTimeout(navigationJourneyRef.current.timerId);
      }
    };
  }, []);
}

/**
 * Hook for keyboard shortcuts (Alt + arrows for history navigation)
 */
export function useNavigationKeyboard() {
  const goBack = useAppStore(state => state.goBackInHistory);
  const goForward = useAppStore(state => state.goForwardInHistory);
  const canGoBack = useAppStore(state => state.canGoBack);
  const canGoForward = useAppStore(state => state.canGoForward);

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Only trigger on Alt + arrow keys
      if (!e.altKey) return;

      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          if (canGoBack()) {
            goBack();
            console.log('⌨️ [useNavigationKeyboard] Alt+←: Go back');
          }
          break;
        case 'ArrowRight':
          e.preventDefault();
          if (canGoForward()) {
            goForward();
            console.log('⌨️ [useNavigationKeyboard] Alt+→: Go forward');
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [goBack, goForward, canGoBack, canGoForward]);
}
